import { Option, Field as UnionField } from '@xo-union/tk-component-fields';
import { FormBlockColumn, FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import Spinner from '@xo-union/tk-component-spinner';
import { Field as FormikField, FieldProps, Formik } from 'formik';
import React, { Suspense, useState } from 'react';
import * as Yup from 'yup';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { H2 } from '@xo-union/tk-ui-typography';

import { useBraintreeTokenQuery } from '../../generated/graphql';
import { Footer } from '../FormComponents';
import mainStyles from './index.scss';
import styles from './paymentForm.scss';
import Select from './Select';

const DropIn = React.lazy(() => import('braintree-web-drop-in-react'));

const PREMIUM_DEFAULT_LENGTH = 1;
const DEFAULT_LENGTH = 2;
const PREMIUM_MAX_LENGTH = 1;
const REDEMPTION_MAX_LENGTH = 1;
const DOT_WEDDING_MAX_LENGTH = 2;
const MAX_LENGTH = 5;

interface Props {
  domainName: string;
  domainPrice: number;
  isPremiumName: boolean;
  isInRedemptionPeriod: boolean;
  description?: string;
  primaryText: string;
  secondaryText: string;
  onCancel: () => void;
  onBuy: (nonce: string, registrationLength: number) => void;
  loading: boolean;
  headerText?: string;
}

const validationSchema = Yup.object().shape({
  registrationLength: Yup.string().required(),
});

const registrationString = (years: number, price: number): string => {
  return `${years} Year${years === 1 ? '' : 's'} (${(years * price).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })})`;
};

const getMaxLength = (isPremiumName: boolean, isInRedemptionPerdiod: boolean, domainName: string): number => {
  if (isPremiumName) {
    return PREMIUM_MAX_LENGTH;
  }

  if (isInRedemptionPerdiod) {
    return REDEMPTION_MAX_LENGTH;
  }

  if (domainName.endsWith('wedding')) {
    return DOT_WEDDING_MAX_LENGTH;
  }

  return MAX_LENGTH;
};

interface PaymentRowProps {
  title: string;
  value: number;
  titleClassName?: string;
  valueClassName?: string;
}

function PaymentSummaryRow({ title, titleClassName, value, valueClassName }: PaymentRowProps): JSX.Element {
  return (
    <FormRow>
      <FormColumn md="6" lg="8">
        <span className={titleClassName}>{title}</span>
      </FormColumn>
      <FormColumn md="6" lg="4">
        <span className={valueClassName}>{value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
      </FormColumn>
    </FormRow>
  );
}

const LoadingAnimation = (): JSX.Element => (
  <div className={styles.loadingAnimation}>
    <Spinner />
  </div>
);

const PaymentForm: React.FC<Props> = ({
  domainName,
  domainPrice,
  isPremiumName,
  isInRedemptionPeriod,
  description,
  primaryText,
  secondaryText,
  onCancel,
  onBuy,
  loading,
  headerText,
}) => {
  const { loading: loadingToken, data } = useBraintreeTokenQuery();
  const [instance, setInstance] = useState<any>();
  const [isBuying, setIsBuying] = useState(false);
  const [registrationLength, setRegistrationLength] = useState(isPremiumName ? PREMIUM_DEFAULT_LENGTH : DEFAULT_LENGTH);
  const maxLength = getMaxLength(isPremiumName, isInRedemptionPeriod, domainName);
  const initialValues = {
    registrationLength: registrationLength.toString(),
  };
  const mobileMedia = useMobileMedia();

  const buy = async () => {
    if (isBuying) return; // prevent double fire
    if (!instance) return; // no drop-in instance yet
    setIsBuying(true);
    try {
      const { nonce } = await instance.requestPaymentMethod();
      await onBuy(nonce, registrationLength);
    } finally {
      setIsBuying(false);
    }
  };

  const total = registrationLength * domainPrice;

  return (
    <>
      <FormRow className={styles.formBody}>
        <FormColumn xs="12" md="6" lg="8">
          <div className={styles.header}>
            <H2 as="h1">{headerText || 'Custom domain'}</H2>
          </div>
          {description ? <div className={styles.subheader}>{description}</div> : null}
          <div className={styles.domainPreviewInput}>
            <UnionField name="Domain" value={domainName} disabled />
          </div>
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={() => buy()}
          >
            {({ submitForm, isSubmitting }): JSX.Element => (
              <>
                <FormBlockColumn>
                  <FormikField name="registrationLength">
                    {(props: FieldProps) => (
                      <div className={styles.overPayment}>
                        <Select
                          label="Registration Length"
                          onChange={({ option: { value } }): void => setRegistrationLength(parseInt(value))}
                          {...props}
                        >
                          {Array.from(Array(maxLength).keys()).map(index => (
                            <Option value={(index + 1).toString()} key={`registrationLength${index}`}>
                              {registrationString(index + 1, domainPrice)}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </FormikField>
                </FormBlockColumn>
                {!loadingToken && !loading ? (
                  <>
                    <div className={styles.paymentDetailsText}>
                      <H2 as="h1">Payment details</H2>
                    </div>
                    <div className={styles.paymentFormBody}>
                      <Suspense fallback={<LoadingAnimation />}>
                        <DropIn options={{ authorization: data?.braintreeToken?.token }} onInstance={setInstance} />
                      </Suspense>
                      <div className={styles.braintreeBadge}>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://www.braintreegateway.com/merchants/${data?.braintreeToken?.vendorId}/verified`}
                        >
                          <img
                            src="https://s3.amazonaws.com/braintree-badges/braintree-badge-wide-light.png"
                            alt="Braintree"
                          />
                        </a>
                      </div>
                    </div>
                    <div className={styles.faq}>
                      <span>Questions? </span>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://helpcenter.theknot.com/hc/en-us/articles/360042645552-Purchase-a-Custom-Domain-for-your-Wedding-Website"
                      >
                        Visit our FAQ
                      </a>
                    </div>
                    <Footer
                      primaryOnClick={submitForm}
                      primaryText={primaryText}
                      primaryDisabled={isSubmitting || isBuying}
                      secondaryOnClick={onCancel}
                      secondaryText={secondaryText}
                      showSpinner={loadingToken || loading}
                      className={mainStyles.footerOverride}
                      buttonsSize={mobileMedia.yes ? 'sm' : 'lg'}
                      footerContainerClassOverride={styles.footerContainerClassOverride}
                    />
                  </>
                ) : (
                  <LoadingAnimation />
                )}
              </>
            )}
          </Formik>
        </FormColumn>
        {mobileMedia.no && (
          <>
            <FormColumn md="1" lg="1" />
            <FormColumn md="5" lg="3">
              <div className={styles.summaryHeader}>
                <div className={styles.purchaseSummaryBox}>
                  <FormRow>
                    <H2 as="h1" className={styles.title}>
                      Order summary
                    </H2>
                  </FormRow>
                  <span className={styles.companyName}>The Knot</span>
                  <PaymentSummaryRow title="Custom domain" value={total} valueClassName={styles.price} />
                  <FormRow>
                    <span className={styles.solid} />
                  </FormRow>
                  <PaymentSummaryRow title="Subtotal" value={total} valueClassName={styles.subTotal} />
                  <PaymentSummaryRow title="Tax" value={0} valueClassName={styles.tax} />
                  <PaymentSummaryRow title="Total" value={total} valueClassName={styles.total} />
                </div>
              </div>
            </FormColumn>{' '}
          </>
        )}
      </FormRow>
    </>
  );
};

export default PaymentForm;
